import { FeatureFlagsHelper } from './FeatureFlagsHelper';


/**
 * There are two types of "feature flags": Development-only flags, and permanent features.
 *
 * "Development" feature flags (features that are being built, for which the flags will be
 * removed once the feature is shipped) belong in this file.
 * ( more info here: https://mytableau.tableaucorp.com/x/3aWaB )
 *
 * Flags for permanent features should not live here; instead, those flags should live on the server
 * or site objects of ISessionInfo, depending on whether they can be toggled at the server or site
 * level.
 *
 * One day this will move from a guideline to a requirement and you will get an urgent bug to
 * move your flag, if it falls into that category.
 *
 * This module is a global object that is as low-level in the app as possible,
 * so we can theoretically put any parameter of the application behind a flag. Flags are specified
 * here, but can be overridden at runtime via URL query parameters.
 */
export module FeatureFlags {

  /**
   * Allows direct assignment of a remote query agent to a data source
   * Created: 2017-03-16 bjudkins
   */
  // remove: TFS 808766
  export const bridgeLiveDirectAssignEnabled = false;

  /**
   * Enables the feature where user can use their OAuth credential to refresh WDC data souces.
   */
  // Created: 2015-10-16 wechen
  // Enabled: 2016-01-07 wechen
  // Disabled: 2016-02-09 mbgumport
  // remove: TFS 808766
  export const oauthWDCServerRefreshEnabled = false;

  // Enables creation of self service schedules
  // Created: 2016-10-24 dcoonan
  // remove: TFS 808776
  // Code pointed this to being used as a server setting instead of a FF.
  // See IFeatureFlagsDoNotUse.ts
  export const selfServiceSchedulesEnabled = false;

  // Enables the feature to create user time zone
  // Created: 2017-03-20 nchenglie
  export const userTimeZoneEnabled = false;

  /**
   * Enables the job management feature.
   */
  // Created: 2018-09-19 nchenglie
  export const jobManagementEnabled = false;

  /*
   * Shows the Certification information on the DS place page.
   */
  // Created: 2017-04-18 eprescott
  // remove: TFS 808701
  export const datasourceCertificationEnabled = true;

  /*
   * Shows a link to disk space usage admin view if enabled
   */
  // Created: 2016-01-11 jchar
  export const lowDiskSpaceEnabled = false;

  /*
   * Display Tableau desktop license usage summary if enabled
   */
  // Created: 2016-03-31 jili
  // Enabled: 2016-04-04 jili
  // remove: TFS 808741
  export const desktopReportingEnabled = true;

  /*
   * Allows subscriptions to be triggered based off of a threshold condition.
   */
  // Created: 2016-06-08 cpredmore
  // Enabled: 2016-09-29 cpredmore
  // Disabled: 2016-10-28 abrandon (TFS: 581467)
  // Enabled: 2017-01-24 tyakushev
  // remove: TFS 808745
  export const alertOnThresholdConditionEnabled = true;

  /*
   * Allows viewing Metrics by proxying out to an external Metrics Server.
   */
  // Created: 2017-06-12 cpredmore
  export const metricsProxyEnabled = false;

  /*
   * Enables Metrics Space Navigation
   */
  // Created: 2019-02-02 alisanti
  export const metricsSpaceEnabled = false;

  /*
   * Enables Metrics Reference Lines
   */
  // Created: 2020-11-24 mdorbin
  export const metricsReferenceLineEnabled = false;

  /*
   * Enables Metrics authoring historical comparisons
   */
  // Created: 2021-07-01 bpshonyak
  export const metricsHistoricalComparisonsEnabled = false;

  /**
   * Enables Metrics lightweight editing
   */
  // Created: 2021-10-20 bpshonyak
  export const metricsEditingEnabled = false;

  /*
   * Allows Tableau support users to be added to a Tableau Cloud site.
   */
  export const addSupportUserEnabled = false;

  /**
   * Allows the server admin to enable the cache warmup feature
   *
   */
  // Created: 2016-09-23 gfilpus
  // Enabled: 2017-03-20 gfilpus
  // remove: TFS 774533
  export const cacheWarmup = true;

  /**
   * Enable "Refresh Now" menu item for remotely-refresh data sources
   * Created: 2016-12-08 bjudkins
   */
  // remove: 808766
  export const dataSyncRefreshNowEnabled = false;

  /**
   * Uses a new, vizportal-based getting started screen, rather than rendering a marketing page.
   * Created: 2016-09-29 cshiraishi
   * Enabled: 2016-11-22 cshiraishi
   */
  // Remove: TFS 775870
  export const newGettingStartedScreen = true;

  /**
   * Shows a custom banner for opening in app for mobile devices on the login page.
   */
  // Created: 2017-03-15
  // Enabled: 2017-03-17
  // remove: TFS 808698
  export const customAppBanner = true;

  /**
   * Shows 'what's new' menu option
   */
  // Created: 2017-04-06 klin
  export const whatIsNewMenuOptionEnabled = false;

  /**
   * Enable renaming views on server (without going into web authoring)
   */
  // Created: 2017-08-07 janlee
  export const renameViewsEnabled = false;

  /**
   * Show hyper instead of dataengine.
   */
  //Created: 2017-05-8 mroberts
  // remove: TFS 808750
  export const hyperOnServerEnabled = false;

  /**
   * Flag to know if new tabadmin services (TSM) feature is enabled (backing feature flag is TabadminService)
   */
  // Created: 2017-05-18 acarre
  // remove: TFS 808750
  export const tableauServicesManagerEnabled = false;

  /**
   * Display data connection tooltip in viz view.
   */
  // Created: 2017-08-16 shubbard
  export const dataConnectionTooltipInVizView = true;

  /**
   * Send analytics info to server
   */
  // Created: 2017-11-21 jfitzgerald
  export const OnlineAnalyticsPipelineEnabled = false;

  /**
   * Display SCIM settings in the authentication tab
   * See: http://l/tfs/697361
   */
  // Created: 2018-02-21 mvafadost
  export const scimApiTokenEnabled = false;

  /**
   *  Show new site settings tab for extensions
   */
  // created: 2018-4-4 jfitzgerald
  export const extensionsSettingsTabEnabled = true;

  /**
   *  Enable/Disable Mentions in Commenting
   */
  // Created: 2018-4-30 nranganna
  export const commentingMentionsEnabled = true;

  /**
   * Show suspend subscription status and allow users to resume suspended subscriptions
   */
  // Created: 2018-09-11 jvanduyne
  // Enabled: 2018-10-29 jvanduyne
  export const suspendSubscriptions = true;

  /**
   * Define policies that all new passwords must comply with.
   */
  // Created: 2018-10-31 fpagliaricci
  // Enabled: 2019-01-22 fpagliaricci
  export const passwordPoliciesEnabled = true;

  /**
   * TFS852547 - Sheets as Tabs Dashboards should preview like Phoenix
   * Mobile Web shows these dashboards with tabs.  Phoenix shows them without tabs.
   */
  // Created: 2018-12-06 majung
  export const SheetsAsTabsDashboardsPreviewLikePhoenix = true;

  /**
   * Make the password expire after a certain amount of days and force the user to reset it.
   */
  // Created: 2018-11-16 fpagliaricci
  // Enabled: 2019-01-22 fpagliaricci
  export const localAuthPasswordExpirationEnabled = true;

  /**
   * Block the user after many failed login attempts and limit the rate of consecutive failed attempts.
   */
  // Created: 2018-12-19 fpagliaricci
  // Enabled: 2019-01-22 fpagliaricci
  export const localAuthLoginRateLimitingEnabled = true;

  /**
   * Allow the user to request RefreshTokens to use in API automation.
   */
  // Created: 2019-04-04 fpagliaricci
  export const personalAccessTokensEnabled = false;

  /**
   * Show a page of recommended vizzes.
   */
  // Created: 2018-12-20 dsimpson
  // Enabled: 2019-06-17 xmao
  export const vizRecommendationsEnabled = true;

  /*
   * Display Tableau IBA license usage summary if enabled
   */
  // Created: 2019-03-01 antsmith
  export const ibaReportingEnabled = false;

  /**
   * Shows the mobile biometrics setting
   */
  // created: 2019-03-18 mmathews
  export const showMobileBiometricsSetting = false;

  /*
   * Share dialog in Vizportal Client for sharing view, workbook, project, datasource
   * Created: 2019-04-11 rkandhal
   * Enabled: 2019-08-02 saramirez
   */
  export const newShareDialogEnabled = true;

  /*
   * Enables the sign in note
   */
  // Created: 2019-04-17 rjoshi
  export const signInNoteEnabled = false;

  /**
   * Enables AFE controls in the settings page
   */
  export const alternativeFederationEngineUserInterface = false;

  /**
   * Enables EAR for flows
   */
  // Created: 2019-05-10 vhalitsyn
  export const maestroEarEnabled = false;

  /**
   * Enable the User Visibiilty feature
   */
  // Created: 2019-06-25 sfan
  export const userVisibilityEnabled = false;

  /**
   * Request Access feature flag
   */
  // Created: 2019-06-19 ayavnilovitch
  export const requestAccessEnabled = false;

  /**
   * Favorite metrics through CRASS
   */
  export const crassFavorites = false;

  /**
   * Enable the all content types tab in search results, powered by crass.
   * Created 2019-09-6 vrobinweiler, #dev-voltron
   */
  export const crassSearch = false;

  /**
   * Show connected objects for dataroles
   */
  // Created: 2019-08-02 csachse
  export const semanticsServiceDataRoleConnectionsEnabled = false;

  /*
   * Enable/Disable the 19.4 UI elements for Job Management
   */
  // Created: 2019-07-24 arama
  export const jobManagement194UIEnabled = false;

  /**
   * Enable/Disbale Managing the ability to create "Run Now" jobs
   */
  // Created: 2019-08-13 arama
  export const enableRunNowEnabled = false;

  /**
   * Metrics content type site setting
   */
  export const metricsContentTypeSiteSettingEnabled = false;

  /**
   * Metrics snapshotting
   */
  export const metricsSnapshottingEnabled = false;

  /*
   * Enables last modified by user for workbook and datasource
   */
  //Created: 2019-09-19 sadhikari
  export const modifiedByUserEnabled = false;

  /**
   * Allow Server Side Refresh via Live Connection to Bridge client when refreshing extracts
   * Created: 2019-09-27 fuliu
   */
  // remove: TFS 986397
  export const bridgeServerSideRefresh = false;

  /**
   * Enables using the SSS dialog to edit admin schedules
   */
  // Created: 2019-10-16 arama
  export const adminSelfServiceSchedulesEnabled = false;

  /**
   * Enables viewing and editing value synonyms for data roles
   */
  // Created: 2019-10-28 csachse
  export const dataRolesEditValueSynonymsEnabled = false;

  /**
   * Enables incremental refresh
   */
  // Created: 2019-10-29 hchen
  export const incrementalRefresh = true;

  /**
   * Enables site quota feature
   */
  // Created: 2019-10-30 sanaik
  export const siteQuotaEnabled = false;

  /**
   * Enables external services site level settings.
   */
  // Created: 2019-11-21 jnegara
  export const extSvcSiteLevelSettingsEnabled = false;

  /**
   * Enables published connection features
   */
  // Created: 2019-11-19 phan
  export const publishedConnectionEditorEnabled = false;

  /**
   * Enables Upload Workbook feature from the Creat content button
   */
  // Created: 2020-01-03 jwonglam
  export const uploadWorkbookFromWebEnabled = false;

  /**
   * Enables Merge/Dedupe feature in Tableau Catalog (Data Catalog)
   */
  // Created: 2020-01-08 jweill
  export const mergeDedupeDatabasesEnabled = false;

  /**
   * Enables published connection content management ship-point 1
   */
  // Created: 2020-01-15 achristian
  export const publishedConnectionContentManagementEnabled = false;

  /**
   * Enables Server Overview Admin View
   */
  // Created: 2020-01-23 sanaik
  export const serverOverviewAdminViewEnabled = false;

  /**
   * Enables Stale Content Admin View
   */
  // Created: 2020-01-23 sanaik
  export const staleContentAdminViewEnabled = true;

  /**
   * Enables Stale Content Admin View Extension
   */
  // Created: 2020-04-21 aolson
  export const staleContentExtensionEnabled = false;

  /**
   * Allows the site admin to set a default site time zone.
   */
  // Created: 2020-03-09 achristian
  export const siteTimeZoneEnabled = true;

  /*
   * Enables the use of Self Service Schedules for Extract Refreshes
   */
  // Created 2020-03-23 arama
  export const selfServiceSchedulesForRefreshEnabled = false;

  /*
   * Enables Workbook Acceleration UI
   */
  // Created 2020-04-15 dguo
  export const workbookAccelerationUIEnabled = false;

  /*
   * Enables View Acceleration Recommendations UI
   */
  // Created 2022-11-15 ymohammed
  export const viewAccelerationRecommendationEngineEnabled = true;

  /**
   * Enabled Prep Extensions site/server settings to control Einstein
   */
  export const prepExtensionsEinstein = true;

  /**
   * Enables Multiple Analytics Extensions Connections UI implemented in 2021.1
   */
  // Created 2020-11-19 jmoens
  export const analyticsExtensionsNamedConnections = false;

  /**
   * Enables enabling/disabling extract refresh auto-suspend feature for data sources
   */
  // Created: 2022-12-16 ross.bechtel
  export const extractRefreshPauseEnabledDatasources = true;

  /**
   * Enables Ask Data Usage Dashboard
   */
  // Created: 2020-06-22 dhruv.khanna
  export const askDataUsageDashboardEnabled = false;

  /**
   * Enables workbook autosave
   */
  // Created: 2021-08-09 michelle.cheung
  export const webAuthoringAutoSaveEnabled = false;

  /**
   * Enables workbook autosave for unpublished workbooks
   */
  // Created: 2023-01-09 beat
  export const webAuthoringAutoSaveForNewWorkbooks = false;

  /**
   * Enables Ask Data Usage and Performance Dashboards for Server Admins
   */
  // Created: 2020-06-22 dhruv.khanna
  export const askDataPerformanceDashboardEnabled = false;

  /**
   * Enables NLPViewerEnablement
   */
  // Created: 2020-07-17 tsmirnov
  export const nlpviewerEnablementEnabled = false;

  /**
   * Allow metric to auto-update without having to manually refresh
   */
  // Created: 2020-06-08 cschaden
  export const metricsAutoUpdateEnabled = false;

  /**
   * Enable copying embed code from share dialog for metrics
   */
  // Created: 2020-08-03 sruthi.pillai
  export const metricsEmbeddingEnabled = false;

  /**
   * Enables metrics integration in catalog
   *
   * Created: 2020-08-04 cbutcher
   */
  export const metricsDataCatalogEnabled = false;

  /**
   * Enables rendering metrics in various colors to determine 'on-track' or
   * 'off-tack', rather than MetricsBlue for all metrics
   */
  // Created: 2020-09-16 lascher
  export const metricsColorIndicatorsEnabled = false;

  /**
   * Enables the Site Expiration Notifications
   */
  // Created: 2020-10-19 qzhuang
  export const siteExpirationNotificationEnabled = false;

  /**
   * Enables Data Quality Warning subscriptions
   */
  // Created: 2020-10-26 odangat
  export const writableFieldDescriptionsEnabled = true;

  /*
   * Enables OAuthClients Registry components
   */
  // Created: 2020-11-25 jwonglam, jinli
  export const siteLevelOAuthClient = false;

  /**
   * Enables searchV2 to show expected UI for semantic search
   */
  // Created: 2020-12-23 kzhuang
  export const searchV2 = true;

  /**
   * Enables International Telemetry for language and locale
   */
  // Created: 2020-12-18 jjun
  export const intlTelemetry = true;

  /**
   * Enables on premise governance VPC components
   */
  // Created: 2021-03-09 nchenglie
  export const onPremiseGovernanceEnabled = true;

  /**
   * Enables the bridge server triggered refresh feature for file based datasources
   */
  // Created: 2021-03-24 amutyala
  export const bridgeServerTriggeredClientSideRefreshForFileBasedDatasourceEnabled = false;

  /**
   * If SLO for OnPrem SiteSAML is enabled.
   */
  // Created: 2021-04-21 fpagliar
  export const siteSamlSLOEnabled = false;

  /**
   * If the user identity migration feature is enabled.
   */
  // Created: 2021-05-12 fpagliar
  export const identityMigrationEnabled = false;

  /**
   * If the identity pools feature is enabled.
   */
  // Created: 2022-06-01 fpagliar
  export const identityPoolsEnabled = false;

  /**
   * Enable UI for CachingPolicies
   */
  // Created: 2021-04-23 dguo
  export const queryPipelineDataFreshnessUISupported = false;

  /**
   * controls Explain Data site setting
   */
  // Created: 2021-05-20 jubooth
  // Created: 2021-07-09 jubooth
  export const explainDataSiteControl = true;

  /**
   * Enable UI for Slack integration setting
   */
  // Created: 2021-05-14 ayavnilovitch
  export const userNotificationSlackChannel = false;

  /**
   * Enable UI for MS Teams UNP setting
   */
  // Created: 2022-11-30 mshaver
  export const msTeamsUserNotification = false;

  /**
   * controls Explain Data permission
   */
  // Created: 2021-05-27 jubooth
  // Enabled: 2021-07-09 jvanduyne
  export const explainDataPermissionEnabled = true;

  /**
   * Enable banner design and disable toast service for trial user (Buy Now function)
   */
  // Created: 2021-06-16 yanyuzhong
  export const trialBannerDesignEnabled = false;

  /**
   * Enables revision history feature for virtual (published) connections
   */
  // Created: 2021-06-29 whudson
  export const vconnRevisionHistoryEnabled = true;

  /**
   * Enables Metrics Authoring Date Window
   */
  // Created: 2021-06-30 sbalasa
  export const metricsDateWindowEnabled = false;

  /**
   * Enables favoriting for Lenses
   */
  // Created: 2021-07-05 lmangibin/szhao
  export const askDataLensFavoritingEnabled = true;

  /**
   * Enables field description inheritance for lineage place pages in Data Catalog
   */
  // Created: 2021-07-27 jweill
  export const fieldDescriptionInheritanceLineageEnabled = false;

  /**
   * Enables Prometheus Predictive Model in server
   */
  // Created: 2021-08-05 xzhao
  export const prometheusPredictiveModelEnabled = false;

  /**
   * Enables deploying the edge pool registry service and enable multiple edge (bridge) pools
   */
  // Created: 2021-08-04 achristian
  export const edgeMultiPoolEnabled = true;

  /*
   * Enables quick search empty state quick links
   */
  // Created: 2021-08-09 amedina
  export const searchQuickLinks = true;

  /*
   * Enables Search product metrics feature.
   */
  // Created: 2022-05-18 llin
  export const searchProductMetrics = true;

  /*
   * Enables Search EPT (experienced page time) measuring feature.
   */
  // Created: 2022-05-18 llin
  export const searchEPT = true;

  /*
   * Enables Offspring Post MVP 22.4 features.
   */
  // Created: 2022-08-01 llin
  export const offspringPostMVP224 = true;

  /*
   * Enables the recent unique users to be the most common of the last 100
   */
  // Created: 2021-08-20 mcolclough
  export const aggregateRecentUsers = false;

  /**
   * Enables flow creation for Catalog spaces e.g. External Assets, and asset place pages.
   */
  // Created: 2021-08-24 jalee
  export const flowsFromCatalogEnabled = true;

  /*
   * Enables recent searches to show in the quick links box
   */
  // Created: 2021-08-25 mcolclough
  export const recentSearches = false;

  /*
   * Enables the site admin task page for data acceleration
   */
  // Created: 2021-08-31 dguo
  export const accelerationSiteAdminUIEnabled = false;

  /*
   * Enables the Lineage tab on published/virtual connections and pconn/vconn tables
   */
  // Created: 2021-09-15 ehaynes
  export const publishedConnectionsLineageEnabled = true;

  export const relevantAssetsEnabled = true;

  /**
   * Enables the Lineage tab for lenses
   */
  // Created: 2021-10-17 csachse
  export const askDataLensesInCatalogEnabled = false;

  /*
   * Enables some of the UI for linked tasks SP2
   */
  // Created: 2021-10-18 jsigo
  export const linkedTasksSP2Enabled = true;

  /**
   * Enables LocationName column in Explore -> Views to use sorting by 'locationName' column. That column is
   * only available in CRASS. When disabled it uses 'containerName' that is availbe in SOLR as well.
   * Disabling for now is requried in GovCloud becasue CRASS is disabled in Govcloud
   */
  // Created: 2021-10-22 ogorenko
  export const LocationNameSortEnabled = false;

  /*
   * Enables inifnite scroll for site users grid
   */
  // Created: 2021-11-17 rjoshi
  export const UserGridInfiniteScrollEnabled = true;

  /*
   * Enables inifnite scroll for server users grid
   */
  // Created: 2021-11-17 rjoshi
  export const serverUserSolrMigrationEnabled = true;

  /**
   * Enables UX around databases in projects
   */
  // Created 2021-11-9 jalee
  export const databasesInProjectsEnabled = true;

  /**
   * Enables broadcast service for the site
   */
  // Created 2021-11-22 ltang
  export const broadcastEnabled = false;

  /**
   * Enables broadcast UI FF
   */
  // Created 2022-08-30 ltang
  // TODO: W-12322280
  export const broadcastUI = false;

  /**
   * Enables "Broadcast UI over REST" FF
   */
  // Created 2023-01-24 jratterman
  // TODO: W-12432395
  export const broadcastUIRest = false;

  /**
   * Per site CMEK scoping flag
   */
  // Created 2022-3-18 vokerlund
  export const cmekAvailableForSite = false;

  /**
   * Enables to move databases to projects
   */
  // Created: 2022-2-14 rachel.liu
  export const databasesInProjectsPostMVPEnabled = true;

  /**
   * Enable multiple Idp for OAuth connectors on server
   */
  // Created: 2022-2-21 eric.wang
  export const oauthMultipleIdpOnServer = true;

  /**
   * Enable BYO Idp for OAuth connectors on server
   */
  // Created: 2022-9-27 ethan.pyke
  export const oauthBYOIdp = true;

  /**
   * Enable Acceleration Improvement Threshold Setting for Auto Suspension
   */
  // Created: 2022-3-10 ymohammed
  export const autoSuspendAccelerationImprovementThresholdSetting = false;

  /**
   * Enable Acceleration Views Limit Setting for Auto Suspension
   */
  // Created: 2022-3-10 ymohammed
  export const autoSuspendAccelerationViewsLimitSetting = false;

  /**
   * Enable Auto Suspension feature
   */
  // Created: 2022-3-13 ymohammed
  export const autoSuspensionForAcceleratedViews = false;

  /**
   * Check if the Tables in projects feature is enabled
   */
  // Created: 2022-4-7 cchudasama
  export const tablesInProjectsEnabled = false;

  /**
   * Check if the Tables in projects UI feature is enabled
   */
  // Created: 2022-5-20 odangat
  export const tablesInProjectsUIEnabled = false;

  /**
   * Enable usage of refreshSession API call instead of getSessionInfo API
   */
  // Created: 2022-05-03 mbarve
  export const refreshSessionEnabled = true;

  /**
   * Enable site settings UI for management of embedded credential migration configuration
   */
  // Created: 2022-3-30 tristan.bithell
  export const embeddedCredentialMigrationEnabled = true;

  /**
   * Enable Data Story site setting
   */
  // Created: 2022-06-06 tj.lewis
  // Enabled: 2022-06-28 tj.lewis
  export const dataStorySiteControl = true;

  /**
   * Enable External Actions Workflows site setting
   */
  // Created: 2022-07-18 sarah.kim
  // Enabled: 2022-08-11 sarah.kim
  export const workflowSiteControl = true;

  /**
   * Pop the GrantPermissionAfterSharingDialog for all content types
   */
  // Created: 2022-4-27 jvanduyne
  // Enabled: 2022-07-20 jvanduyne
  export const grantPermissionsAfterSharing = true;

  /*
   * Enables mixed content search on connect to dialog
   */
  // Created: 2022-08-08 slella
  export const mixedContentConnectToDialogEnabled = true;

  /**
   *  Exposes workbook usage data in the form of metric content type to be displayed on the workbook page
   */
  // Created: 2022-8-9 nicholasshepherd - Stylish
  export const authorMetricsEnabled = false;

  /*
   * Enables REST APIs for custom views management
   */
  // Created: 2022-08-23 ghaffar.balogun
  export const customViewRestApiEnabled = false;

  /**
   * Enabling passing user attributes and evaluating user functions against the attributes
   */
  // Created: 2022-09-19 AndyLiu(zhiheng.liu@tableau.com)
  export const userAttributeFunctionsEnabled = false;

  /*
   * Enables enhanced acceleration tooltip
   */
  // Created: 2022-09-15 dguo
  export const enhancedAccelerationTooltip = false;

  /**
   * Enables Prep Conductor Awareness materials
   */
  export const prepConductorAwarenessEnabled = false;

  /**
   * Enables Prep Conductor Awareness materials
   */
  // Created: 2022-10-25 jinli
  export const snowflakeKeyPairAuth = false;

  /**
   * Enables Smarter Search features
   */
  // Created: 2022-10-27 edeiparine
  export const smarterSearchEnabled = true;

  /**
   * Enables Search Drafts features
   */
  // Created: 2023-02-17 edeiparine
  export const searchDraftsEnabled = true;

  /**
   * Enables Bi-Directional slack integration
   */
  // Created: 2022-10-31 jball
  export const slackIntegrationPlatformEnabled = false;

  /**
   * Enable Data Orientation admin settings
   */
  // Created: 2022-11-07 jose.sarmiento
  export const dataOrientationAdminSettings = true;

  /**
   * Move the GrantPermissionAfterSharingDialog into the share-dialog module
   */
  // Created: 2022-11-08 jvanduyne
  export const sharePermissionsVizclientEnabled = false;

  /*
   * Enables hybrid live and extract virtual connections
   */
  // Created: 2022-11-14 gabriela.pulido
  export const hybridLiveExtractVConnsEnabled = false;

  /**
   * Enable the global NTBUE Analyst Flow
   */
  // Created 2022-11-28 szhao
  export const ntbueGlobalEnabled = false;

  /*
   * Enables custom domains in site settings
   */
  // Created: 2022-11-09 smalikireddy
  export const adminExpCustomDomainsEnabled = true;

  /**
   * Enable the data management awareness dialogs
   */
  // Created 2022-11-29 slella
  export const dmawarenessInExploreEnabled = false;

  /*
   * Enables run history subspace for virtual connections
   */
  // Created: 2022-12-08 gabriela.pulido
  export const vconnRunHistory = false;

  /**
   * Enable Data Story site setting for non-SaaS environments
   */
  // Created: 2022-12-15 tj.lewis
  export const dataStoryAvailableOnPrem = true;

  /**
   * Enables Uni-Directional Microsoft Teams integration
   */
  // Created: 2023-01-23 sarah.kim
  export const msTeamsIntegrationPlatformEnabled = false;

  /**
   * Enables the site setting to allow disabling of links in Subscriptions and DDAs
   */
  // Created: 2023-01-26 aolson
  export const removeSubscriptionAndDDAEmailLinksEnabled = false;

  /**
   * Enable Flow output timestamps
   */
  // Created: 2023-02-01 jjia
  export const timestampOutput = true;

  /**
  * Enable PAT Access Control
  */
  // Created: 2023-02-07 xyin
  export const patCreationControlEnabled = false;

  /**
   * Enable Refresh Token Absolute Expiry as a site-setting
   */
  // Created: 2023-02-07 ruby.werman
  export const refreshTokenAbsoluteExpiryEnabled = false;

  /**
   * Enable search and filter on lineage page
   */
  //created: 2023-03-07 y.ding
  export const searchAndFilterOnLineagePageEnabled = true;

   /**
   * Enable label management
   */
  //created: 2023-04-13 zchai
  export const labelManagementEnabled = false;

  /**
   * Enable the new Data Cloud UI changes
   */
  // Created: 2023-04-20 cchudasama
  export const dataCloudInCatalogMVPEnabled = true;

  /**
   * Enable auto quality monitoring site settings
   */
  // Created: 2023-04-27 linda.nduhiu
  export const autoQualityMonitoringEnabled = true;

  /**
   * Enable labels in Web Authoring
   */
  // Cfreated: 2023-05-04 afeyissa
  export const labelsInWebAuthoringEnabled = true;

   /**
   * Enables the new Edit Group Dialog Action UI
   */
  // Created: 2023-04-20 rjoshi
  export const editGroupRedesignEnabled = true;

  /**
   * Enable Custom Label
   */
  // Cfreated: 2023-06-07 csheng
  export const customLabelCategoriesEnabled = true;


  /**
   * Enables gating of feature work for Email Customization Milestone 2 POC
   */
  // Created: 2023-03-27 nicholasshepherd
  export const emailCustomizationEnabled = false;

  /**
   * Enables beacon search in omnibox-search
   */
  // Created: 2023-06-15 sloka
  export const beaconSearchEnabled = false;

  /**
   * Enables Connected Apps Multi-project
   */
  // Created: 2023-07-24 xyin
  export const connectedAppsMultiProjectEnabled = true;

  /**
   * Enables Multi-EAS Per Site
   */
  // Created: 2023-07-25 agao
  export const multiEASPerSiteEnabled = true;

  /**
   * ABAC Lite feature flag
   */
  // Created: 2023-08-15 pgreen
  export const permissionsGroupLogicalAndEnabled = false;

    /**
   * Asserted Local Groups feature flag
   */
  // Created: 2023-08-15 pgreen
  export const abacassertedLocalGroupsEnabled = false;

  /**
   * Enables custom schedules for flows
   */
  // Created; 2023-06-05 sharanyapillalamarri, malonso
  export const selfServiceSchedulesForFlow = false;

  /**
   * Enable generated suggestions for content descriptions
   */
  // Created; 2023-08-11 linda.nduhiu
  export const suggestDescriptionsEnabled = true;

  /**
   * Enable Tableau AI site setting
   */
  // Created: 2023-10-20 m.tobitchimi
  export const generativeAiSiteControl = true;

  /**
   * Enable admin lockout with external IdP
   */
  // Created; 2023-11-02 edeiparine
  export const adminLockoutEnabled = true;

  /**
   * Enable site generic OIDC
   */
  // Created; 2023-10-20 george.shin
  export const genericOIDCEnabled = false;

  /**
   * Enable Bridge as a Service
   */
  // Created; 2023-11-16 mahaa.noorani
  export const distributedCloudBridgeAsAServiceEnabled = false;

  /**
   * Enable the personalised insight ranking site setting for Pulse
   */
  // Created: 2023-11-15 m.tobitchimi
  export const pulsePersonalizedRankingSiteControl = true;

  /**
   * Allow admins to control who sees the Pulse feature UI
   */
  // Created: 2024-01-02 bconway
  export const pulseSiteControlEnabled = true;

  /**
   * Show a message that Bridge Legacy Schedules will be deprecated
   */
  // Created: 2024-01-09 l.zhou
  export const bridgeLegacySchedulesRetirementMessageEnabled = true;

  /**
   * Enable common tabgai infrastructure
   */
  // Created: 2024-01-05 jhallmann
  export const tabGAITokenService = false;

  /**
   * Enable tabgai registration
   */
    // Created: 2024-01-05 jhallmann
  export const tabGAITokenServiceRegistration = true;

  /**
   * Enable tabgai enterprise registration
   */
  // Created: 2024-01-05 jhallmann
  export const tabGAITokenServiceEnterpriseRegistration = true;

  /**
   * Enable tabgai enterprise registration
   */
  // Created: 2024-02-20 jhallmann
  export const ntbueAIEnhancedLanguage = true;

  /**
   * Enable address geocoding (named geocodingExtension to match the monolith FF)
   */
  // Created: 2024-01-29 dianawang
  export const geocodingExtension = false;

  /**
   * Enable data connect
   */
  // Created: 2024-02-26 biliu
  export const enableDataConnect = false;

    /**
   * Enable link features in UI
   */
  // Created: 2024-03-03 rjoshi
  export const syncToLinkEnabled = false;

  /**
   * Enable virtual connection incremental extract
   */
  // Created: 2024-03-16 nlavender
  export const vconnIncrementalRefreshEnabled = true;

  /**
   * Enable pulse metric definition in catalog
   */
  // Created: 2024-03-20 y.ding
  export const pulseInDataCatalogEnabled = false;

  /**
   * Enable private connect
   */
    // Created: 2024-03-25 jason.liu
  export const enablePrivateConnect = false;

  /**
   * Multiple IdP UI Support
   */
  // Created 2024-03-26 edeiparine
  export const featureFlagForMultipleIDPSupportEnabled = false;

  /**
   * Enable web authoring AI/Einstein Copilot for Tableau(ECT) checkbox in site settings
   */
  // Created: 2024-04-02 xjia 
  export const webAuthoringAISettingEnabled = false;
}

// Override the feature flags as soon as possible after the page loads and the feature flag defaults
// are set, before the application is initialized. The helper must be invoked after declaration of
// the feature flags, otherwise there will not be existing values to overwrite
if (window.location.search) {
  FeatureFlagsHelper.overwriteFeatureFlagsFromQueryString(window.location.search, FeatureFlags);
}
